import {
  Dropdown,
  Field,
  makeStyles,
  Option,
  SpinButton,
  Switch,
  tokens,
} from '@fluentui/react-components';
import { useT } from '@powerportalspro/react';
import {
  ChoiceOrientation,
  LookupEdit,
  LookupEditorType,
  Section,
  SectionColumn,
} from '@powerportalspro/react-fluent';
import { useMemo, useState } from 'react';

// Localization namespace — reuses the Blazor demo's
// `LookupEditPropertiesDemo` keys so both stacks read from the same JSON.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Editors.LookupEdit.LookupEditPropertiesDemo';

const EDITOR_TYPE_VALUES = [
  LookupEditorType.AutoComplete,
  LookupEditorType.Dropdown,
  LookupEditorType.RadioButtons,
] as const;
const ORIENTATION_VALUES = [
  ChoiceOrientation.Vertical,
  ChoiceOrientation.Horizontal,
] as const;

// Mirrors the Blazor demo's seed list. Localized via the global namespace
// (tables.<name>.label) — same source the Blazor demo's `_globalLocalizer`
// reads from.
const TABLE_VALUES = ['contact', 'account'] as const;
type TableValue = (typeof TABLE_VALUES)[number];

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
  },
  inlineField: {
    flexShrink: 0,
    gridTemplateColumns: 'auto auto',
    '& label': {
      whiteSpace: 'nowrap',
    },
  },
  selectInput: {
    minWidth: '125px',
  },
  numberInput: {
    width: '85px',
  },
});

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function LookupEditPropertiesSample() {
  const styles = useStyles();
  const t = useT();
  const [readOnly, setReadOnly] = useState(false);
  const [required, setRequired] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [displayNullChoice, setDisplayNullChoice] = useState(true);
  const [maxRecordsReturned, setMaxRecordsReturned] = useState(10);
  // Blazor demo defaults to "account".
  const [defaultTable, setDefaultTable] = useState<TableValue>('account');
  const [editorType, setEditorType] = useState<LookupEditorType>(LookupEditorType.AutoComplete);
  const [orientation, setOrientation] = useState<ChoiceOrientation>(ChoiceOrientation.Vertical);
  const [wrapRadioButtons, setWrapRadioButtons] = useState(true);

  const tableOptions = useMemo(
    () =>
      TABLE_VALUES.map((value) => ({
        value,
        label: t(`tables.${value}.label`),
      })),
    [t],
  );
  const editorTypeOptions = useMemo(
    () =>
      EDITOR_TYPE_VALUES.map((value) => ({
        value,
        label: t(`${STRINGS_BASE}.lookupEditorType.${cap(value)}`),
      })),
    [t],
  );
  const orientationOptions = useMemo(
    () =>
      ORIENTATION_VALUES.map((value) => ({
        value,
        label: t(`${STRINGS_BASE}.orientation.${cap(value)}`),
      })),
    [t],
  );

  const autoCompleteStyle =
    editorType === LookupEditorType.AutoComplete ? { maxWidth: 300 } : {};

  return (
    <Section>
      <SectionColumn
        header={
          <div className={styles.toolbar}>
            <Switch
              label={t(`${STRINGS_BASE}.readonly`)}
              labelPosition="before"
              checked={readOnly}
              onChange={(_, d) => setReadOnly(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.required`)}
              labelPosition="before"
              checked={required}
              onChange={(_, d) => setRequired(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.disabled`)}
              labelPosition="before"
              checked={disabled}
              onChange={(_, d) => setDisabled(d.checked)}
            />
            <Field
              label={t(`${STRINGS_BASE}.default-table`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.selectInput}
                value={tableOptions.find((o) => o.value === defaultTable)?.label ?? defaultTable}
                selectedOptions={[defaultTable]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue) setDefaultTable(d.optionValue as TableValue);
                }}
              >
                {tableOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Field
              label={t(`${STRINGS_BASE}.max-records`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <SpinButton
                className={styles.numberInput}
                min={1}
                value={maxRecordsReturned}
                onChange={(_, d) => {
                  const n = typeof d.value === 'number' ? d.value : Number(d.displayValue);
                  if (Number.isFinite(n)) setMaxRecordsReturned(n);
                }}
              />
            </Field>
            <Field
              label={t(`${STRINGS_BASE}.lookup-editor-type`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.selectInput}
                value={
                  editorTypeOptions.find((o) => o.value === editorType)?.label ?? editorType
                }
                selectedOptions={[editorType]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue) setEditorType(d.optionValue as LookupEditorType);
                }}
              >
                {editorTypeOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            {editorType !== LookupEditorType.AutoComplete && (
              <Switch
                label={t(`${STRINGS_BASE}.display-null-choice`)}
                labelPosition="before"
                checked={displayNullChoice}
                onChange={(_, d) => setDisplayNullChoice(d.checked)}
              />
            )}
            {editorType === LookupEditorType.RadioButtons && (
              <Field
                label={t(`${STRINGS_BASE}.radio-buttons-orientation`)}
                orientation="horizontal"
                className={styles.inlineField}
              >
                <Dropdown
                  className={styles.selectInput}
                  value={
                    orientationOptions.find((o) => o.value === orientation)?.label ?? orientation
                  }
                  selectedOptions={[orientation]}
                  onOptionSelect={(_, d) => {
                    if (d.optionValue) setOrientation(d.optionValue as ChoiceOrientation);
                  }}
                >
                  {orientationOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            )}
            {editorType === LookupEditorType.RadioButtons && orientation === ChoiceOrientation.Horizontal && (
              <Switch
                label={t(`${STRINGS_BASE}.wrap-radio-buttons`)}
                labelPosition="before"
                checked={wrapRadioButtons}
                onChange={(_, d) => setWrapRadioButtons(d.checked)}
              />
            )}
          </div>
        }
      >
        {/* Radio buttons take the section's width, so a horizontal row has room to wrap or scroll in. */}
        <div style={editorType === LookupEditorType.RadioButtons ? undefined : { maxWidth: '320px' }}>
          <LookupEdit
            columnName="ppp_parentcustomerid"
            required={required}
            readOnly={readOnly}
            disabled={disabled}
            defaultTable={defaultTable}
            displayNullChoice={displayNullChoice}
            editorType={editorType}
            radioButtonOrientation={orientation}
            wrapRadioButtons={wrapRadioButtons}
            maxRecordsReturned={maxRecordsReturned}
            style={autoCompleteStyle}
          />
        </div>
      </SectionColumn>
    </Section>
  );
}
